import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "../../components/ui/table";
import { 
  Receipt, Search, Loader2, Eye, CheckCircle2, XCircle, Clock,
  CreditCard, Building2, Calendar, Euro
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { formatCurrency, formatDate } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const STATUS_BADGES = {
  pending: { label: "In attesa", className: "bg-yellow-100 text-yellow-800", icon: Clock },
  completed: { label: "Completato", className: "bg-green-100 text-green-800", icon: CheckCircle2 },
  failed: { label: "Fallito", className: "bg-red-100 text-red-800", icon: XCircle },
  cancelled: { label: "Annullato", className: "bg-gray-100 text-gray-800", icon: XCircle }
};

export default function AdminTransactions() {
  const { token } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  useEffect(() => {
    if (token) {
      fetchTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchTransactions = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/admin/transactions`, { headers, withCredentials: true });
      setTransactions(res.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Errore nel caricamento transazioni");
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = transactions.filter((t) => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!t.transaction_id.toLowerCase().includes(search) && 
          !t.company_name?.toLowerCase().includes(search)) {
        return false;
      }
    }
    if (statusFilter !== "all" && t.status !== statusFilter) {
      return false;
    }
    return true;
  });

  const openDetail = async (transaction) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/admin/transactions/${transaction.transaction_id}`, { headers, withCredentials: true });
      setSelectedTransaction(res.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error("Errore nel caricamento dettagli");
    }
  };

  // Stats
  const stats = {
    total: transactions.length,
    completed: transactions.filter(t => t.status === "completed").length,
    pending: transactions.filter(t => t.status === "pending").length,
    revenue: transactions.filter(t => t.status === "completed").reduce((sum, t) => sum + (t.amount || 0), 0)
  };

  return (
    <DashboardLayout title="Transazioni">
      <div className="space-y-6" data-testid="admin-transactions">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Totale</p>
                  <p className="text-2xl font-bold font-mono">{stats.total}</p>
                </div>
                <Receipt className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completate</p>
                  <p className="text-2xl font-bold font-mono text-green-600">{stats.completed}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">In Attesa</p>
                  <p className="text-2xl font-bold font-mono text-yellow-600">{stats.pending}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Ricavi Totali</p>
                  <p className="text-2xl font-bold font-mono text-primary">{formatCurrency(stats.revenue)}</p>
                </div>
                <Euro className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca per ID o azienda..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtra per stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="pending">In Attesa</SelectItem>
              <SelectItem value="completed">Completate</SelectItem>
              <SelectItem value="failed">Fallite</SelectItem>
              <SelectItem value="cancelled">Annullate</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Transactions Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="py-12 text-center">
                <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nessuna transazione trovata</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID Transazione</TableHead>
                    <TableHead>Azienda</TableHead>
                    <TableHead>Piano</TableHead>
                    <TableHead>Metodo</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-right">Importo</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.map((t) => {
                    const statusBadge = STATUS_BADGES[t.status] || STATUS_BADGES.pending;
                    const StatusIcon = statusBadge.icon;
                    
                    return (
                      <TableRow key={t.transaction_id}>
                        <TableCell className="font-mono text-sm">
                          {t.transaction_id}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            {t.company_name || "N/A"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{t.plan_name || t.plan_id}</Badge>
                        </TableCell>
                        <TableCell className="capitalize">
                          <div className="flex items-center gap-1">
                            <CreditCard className="h-4 w-4 text-muted-foreground" />
                            {t.payment_method}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {formatDate(t.created_at)}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusBadge.className}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusBadge.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold">
                          {formatCurrency(t.amount)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDetail(t)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Dettaglio Transazione</DialogTitle>
            </DialogHeader>
            {selectedTransaction && (
              <div className="space-y-6">
                {/* Transaction Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">ID Transazione</p>
                    <p className="font-mono font-bold">{selectedTransaction.transaction_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Stato</p>
                    <Badge className={STATUS_BADGES[selectedTransaction.status]?.className}>
                      {STATUS_BADGES[selectedTransaction.status]?.label}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Importo</p>
                    <p className="text-xl font-bold font-mono">{formatCurrency(selectedTransaction.amount)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Metodo</p>
                    <p className="capitalize">{selectedTransaction.payment_method}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Piano</p>
                    <p>{selectedTransaction.plan_name} ({selectedTransaction.months} {selectedTransaction.months === 1 ? "mese" : "mesi"})</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Data</p>
                    <p>{formatDate(selectedTransaction.created_at)}</p>
                  </div>
                </div>

                {/* Company Info */}
                {selectedTransaction.company && (
                  <div className="border-t pt-4">
                    <h4 className="font-bold mb-3">Azienda</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Nome</p>
                        <p>{selectedTransaction.company.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <p>{selectedTransaction.company.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">P.IVA</p>
                        <p>{selectedTransaction.company.vat_number || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Città</p>
                        <p>{selectedTransaction.company.city}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Billing Info */}
                {selectedTransaction.billing_info && Object.keys(selectedTransaction.billing_info).length > 0 && (
                  <div className="border-t pt-4">
                    <h4 className="font-bold mb-3">Dati Fatturazione</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedTransaction.billing_info.company_name && (
                        <div>
                          <p className="text-sm text-muted-foreground">Ragione Sociale</p>
                          <p>{selectedTransaction.billing_info.company_name}</p>
                        </div>
                      )}
                      {selectedTransaction.billing_info.vat_number && (
                        <div>
                          <p className="text-sm text-muted-foreground">P.IVA</p>
                          <p>{selectedTransaction.billing_info.vat_number}</p>
                        </div>
                      )}
                      {selectedTransaction.billing_info.address && (
                        <div>
                          <p className="text-sm text-muted-foreground">Indirizzo</p>
                          <p>{selectedTransaction.billing_info.address}</p>
                        </div>
                      )}
                      {selectedTransaction.billing_info.pec && (
                        <div>
                          <p className="text-sm text-muted-foreground">PEC</p>
                          <p>{selectedTransaction.billing_info.pec}</p>
                        </div>
                      )}
                      {selectedTransaction.billing_info.sdi_code && (
                        <div>
                          <p className="text-sm text-muted-foreground">Codice SDI</p>
                          <p>{selectedTransaction.billing_info.sdi_code}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Payment IDs */}
                <div className="border-t pt-4">
                  <h4 className="font-bold mb-3">Riferimenti Pagamento</h4>
                  <div className="space-y-2 text-sm">
                    {selectedTransaction.stripe_session_id && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Stripe Session ID:</span>
                        <span className="font-mono">{selectedTransaction.stripe_session_id}</span>
                      </div>
                    )}
                    {selectedTransaction.paypal_order_id && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">PayPal Order ID:</span>
                        <span className="font-mono">{selectedTransaction.paypal_order_id}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
